"use strict";
/**
 * STT (Speech-to-Text) Field Schemas — AI Transcription step.
 *
 * Build-time constants describing the user-configurable fields on the AI
 * Transcription node. These are projected onto `NodeIOSlot` at step-insert
 * time via `buildSttInputSlots()`; after that, the snapshot carries all
 * options/defaults inline (see `slot.ts` — marketplace portability).
 *
 * Values map to the managed transcription provider config:
 *   - languageCode               → config.languageCodes[0]
 *   - enableAutomaticPunctuation → config.features.enableAutomaticPunctuation
 *   - profanityFilter            → config.features.profanityFilter
 *
 * Extending language list: add an entry here — `buildSttInputSlots()`
 * pulls it through. Pre-existing workflows keep their frozen options
 * (self-descriptive) — only freshly-inserted or explicitly-refreshed
 * steps pick up the new language.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.DEFAULT_DYNAMIC_BATCHING = exports.DEFAULT_STT_CONFIG = exports.STT_FIELD_SCHEMAS = exports.STT_LANGUAGE_OPTIONS = void 0;
exports.sttSlotId = sttSlotId;
exports.slotFromSchema = slotFromSchema;
exports.buildSttInputSlots = buildSttInputSlots;
exports.buildSttConfigSlots = buildSttConfigSlots;
exports.projectSttConfig = projectSttConfig;
const brand_1 = require("@streamlain/brand");
/**
 * BCP-47 language codes supported by StreamLain Transcription.
 * `auto` uses the managed provider's language-agnostic transcription path.
 *
 * Labels are English by design — they travel with the snapshot and the
 * editor shows them unchanged on import. An i18n pass may later add
 * translation keys; values are immutable.
 */
exports.STT_LANGUAGE_OPTIONS = [
    { value: 'auto', label: 'Auto' },
    { value: 'de-DE', label: 'German (Germany)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-GB', label: 'English (UK)' },
    { value: 'fr-FR', label: 'French' },
    { value: 'es-ES', label: 'Spanish' },
    { value: 'it-IT', label: 'Italian' },
    { value: 'nl-NL', label: 'Dutch' },
    { value: 'pt-BR', label: 'Portuguese (Brazil)' },
    { value: 'pl-PL', label: 'Polish' },
    { value: 'ja-JP', label: 'Japanese' },
    { value: 'ko-KR', label: 'Korean' },
    { value: 'zh-CN', label: 'Chinese (Simplified)' },
];
/**
 * Field schemas keyed by slot name. The key IS the runtime input property
 * name the runner passes to the provider.
 */
exports.STT_FIELD_SCHEMAS = {
    languageCode: {
        type: 'enum',
        defaultValue: 'auto',
        options: exports.STT_LANGUAGE_OPTIONS,
        label: 'Language',
        labelsI18n: {
            en: 'Language',
            de: 'Sprache',
        },
        description: 'BCP-47 language hint for transcription. Auto works best for ' +
            'most single-language recordings.',
        descriptionsI18n: {
            en: 'BCP-47 language hint for transcription. Auto works best for ' +
                'most single-language recordings.',
            de: 'Sprachhinweis fuer die Transkription. Auto ist ' +
                'fuer die meisten einsprachigen Aufnahmen die beste Wahl.',
        },
    },
    enableAutomaticPunctuation: {
        type: 'boolean',
        defaultValue: true,
        label: 'Automatic Punctuation',
        labelsI18n: {
            en: 'Automatic Punctuation',
            de: 'Automatische Zeichensetzung',
        },
        description: 'Insert full stops, commas, and question marks into the transcript.',
        descriptionsI18n: {
            en: 'Insert full stops, commas, and question marks into the transcript.',
            de: 'Fuegt Punkte, Kommas und Fragezeichen automatisch in das Transkript ein.',
        },
    },
    enableSpeakerDiarization: {
        type: 'boolean',
        defaultValue: false,
        label: 'Speaker Diarization',
        labelsI18n: {
            en: 'Speaker Diarization',
            de: 'Sprechererkennung',
        },
        description: 'Attach speaker labels (e.g. "Speaker 1:", "Speaker 2:") when more ' +
            'than one voice is present.',
        descriptionsI18n: {
            en: 'Attach speaker labels (e.g. "Speaker 1:", "Speaker 2:") when more ' +
                'than one voice is present.',
            de: 'Fuegt Sprecherlabels hinzu, wenn mehr als eine Stimme erkannt wird.',
        },
    },
    profanityFilter: {
        type: 'boolean',
        defaultValue: false,
        label: 'Profanity Filter',
        labelsI18n: {
            en: 'Profanity Filter',
            de: 'Schimpfwortfilter',
        },
        description: 'Replace strong language with asterisks in the transcript.',
        descriptionsI18n: {
            en: 'Replace strong language with asterisks in the transcript.',
            de: 'Ersetzt starke Sprache im Transkript durch Sternchen.',
        },
    },
    // sl-67kc.1.5: Opt into managed deferred processing. When enabled the
    // workflow row is suspended until the callback resumes it (up to 25h). The
    // runner reads this flag separately from `SttConfig` (provider-level
    // config) — see `dynamicBatchingFromSlots`.
    //
    // UI disables this slot when the selected model lacks the 'batch'
    // capability or the workspace plan is below Pro; see the panel banner
    // (`stt-plan-hint`). Backend enforces the same plan gate at submit
    // time so a tampered slot still fails closed.
    dynamicBatching: {
        type: 'boolean',
        defaultValue: false,
        label: 'Deferred processing (lower cost)',
        labelsI18n: {
            en: 'Deferred processing (lower cost)',
            de: 'Zeitversetzte Verarbeitung (guenstiger)',
        },
        description: 'Use lower-cost asynchronous processing over up to 24h. Workflow execution ' +
            `is suspended until ${brand_1.BRAND_DISPLAY_NAME} reports completion. Requires Pro+.`,
        descriptionsI18n: {
            en: 'Use lower-cost asynchronous processing over up to 24h. Workflow ' +
                `execution is suspended until ${brand_1.BRAND_DISPLAY_NAME} reports completion. Requires Pro+.`,
            de: 'Nutzt guenstigere asynchrone Verarbeitung. Die Verarbeitung kann ' +
                'bis zu 24 Stunden dauern; der Workflow wartet automatisch auf die ' +
                `${brand_1.BRAND_DISPLAY_NAME}-Fertigmeldung. Erfordert Pro+.`,
        },
    },
};
/**
 * Defaults for the runtime STT config. Mirrors the `defaultValue` on each
 * field schema. Used as the starting point before applying per-slot
 * resolution — keeps all defaults in one place (DRY).
 */
exports.DEFAULT_STT_CONFIG = {
    languageCode: exports.STT_FIELD_SCHEMAS.languageCode.defaultValue,
    enableAutomaticPunctuation: exports.STT_FIELD_SCHEMAS.enableAutomaticPunctuation.defaultValue,
    enableSpeakerDiarization: exports.STT_FIELD_SCHEMAS.enableSpeakerDiarization.defaultValue,
    profanityFilter: exports.STT_FIELD_SCHEMAS.profanityFilter.defaultValue,
};
/**
 * Deterministic slot-id scheme for fixed (non-user-editable) slot names.
 * ID is a pure function of (stepId, fieldKey) so re-materialising a slot
 * preserves identity and stored bindings survive editor reloads.
 *
 * Mirrors `fixedFieldId` in the frontend's `step-types/helpers.ts`. Both
 * callers MUST produce the same string, so this is the single definition
 * — the helpers.ts version delegates here once both sides are on the
 * shared package. Format is `field-{stepId}-{fieldKey}`.
 */
function sttSlotId(stepId, fieldKey) {
    return `field-${stepId}-${fieldKey}`;
}
/**
 * Materialise a single slot from a field schema. Freezes schema values
 * into the slot so it becomes self-descriptive.
 *
 * `id` is deterministic (`stepId:name`) so re-materialising at editor
 * load-time does not break stored input mappings.
 */
function slotFromSchema(stepId, name, schema, overrides) {
    // FieldSchema authors locale maps when available; scalar label/description
    // stay legacy mirrors. The map shape remains open-ended so future user
    // locales can travel with published node definitions without code changes.
    return {
        id: sttSlotId(stepId, name),
        name,
        labelsI18n: schema.labelsI18n ?? { en: schema.label ?? name },
        type: schema.type,
        required: schema.required ?? false,
        description: schema.description,
        descriptionsI18n: schema.descriptionsI18n,
        options: schema.options,
        defaultValue: schema.defaultValue,
        bindable: schema.bindable,
        ...overrides,
    };
}
/**
 * Build the input-slot list for an AI step with `capability: 'stt'`.
 *
 * The STT node has exactly one input slot — the audio/video file to
 * transcribe. Runtime knobs such as language, punctuation and profanity are
 * **config slots**, not inputs, and live on `step.configSlots` via
 * `buildSttConfigSlots()`. This split mirrors the slot-based
 * `NodeTypeDefinition` shape (sl-ngy4).
 */
function buildSttInputSlots(stepId) {
    const audioFile = {
        id: sttSlotId(stepId, 'audioFile'),
        name: 'audioFile',
        labelsI18n: {
            en: 'Audio File',
            de: 'Audiodatei',
        },
        type: 'attachment',
        required: true,
        description: 'Audio or video file to transcribe.',
        descriptionsI18n: {
            en: 'Audio or video file to transcribe.',
            de: 'Audio- oder Videodatei, die transkribiert werden soll.',
        },
    };
    return [audioFile];
}
/**
 * Build the config-slot list for an AI step with `capability: 'stt'`.
 *
 * Materialises the currently supported `STT_FIELD_SCHEMAS` entries into
 * self-descriptive slots. Consumed by the runner via `resolveFieldValue()` +
 * `projectSttConfig()` and by the frontend panel renderer. Descriptor-level
 * callers MUST use this for the config surface — user values survive
 * marketplace import/export without touching any registry.
 */
const STT_CONFIG_SLOT_KEYS = Object.keys(exports.STT_FIELD_SCHEMAS).filter(key => key !== 'enableSpeakerDiarization');
function buildSttConfigSlots(stepId) {
    return STT_CONFIG_SLOT_KEYS.map(key => slotFromSchema(stepId, key, exports.STT_FIELD_SCHEMAS[key], {
        // sl-c9oi.6: scalar STT configs render through SlotMapper's unified row
        // picker (constant + upstream binding via template string + custom
        // value). No explicit `widget` — `undefined` picks the picker row.
        bindable: true,
    }));
}
/**
 * Project a resolved map of input values onto a typed `SttConfig`. Callers
 * (runner) pass the map built via `resolveFieldValue()` over every slot;
 * this function narrows it to the runtime shape expected by the provider.
 *
 * Unknown / missing keys fall back to `DEFAULT_STT_CONFIG` so the provider
 * never sees undefined.
 *
 * `dynamicBatching` is intentionally NOT part of `SttConfig` — it is a
 * runner-level submit-tier switch, not a provider request field. Callers
 * read it separately via `dynamicBatchingFromSlots`.
 */
function projectSttConfig(resolved) {
    return {
        languageCode: typeof resolved['languageCode'] === 'string'
            ? resolved['languageCode']
            : exports.DEFAULT_STT_CONFIG.languageCode,
        enableAutomaticPunctuation: typeof resolved['enableAutomaticPunctuation'] === 'boolean'
            ? resolved['enableAutomaticPunctuation']
            : exports.DEFAULT_STT_CONFIG.enableAutomaticPunctuation,
        enableSpeakerDiarization: typeof resolved['enableSpeakerDiarization'] === 'boolean'
            ? resolved['enableSpeakerDiarization']
            : exports.DEFAULT_STT_CONFIG.enableSpeakerDiarization,
        profanityFilter: typeof resolved['profanityFilter'] === 'boolean'
            ? resolved['profanityFilter']
            : exports.DEFAULT_STT_CONFIG.profanityFilter,
    };
}
/**
 * sl-67kc.1.5: Default for the runner-level `dynamicBatching` switch.
 * Kept alongside `DEFAULT_STT_CONFIG` so all STT defaults live in one
 * place even though the value sits outside the provider config shape.
 */
exports.DEFAULT_DYNAMIC_BATCHING = exports.STT_FIELD_SCHEMAS.dynamicBatching.defaultValue;
